import { useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { TabTile, TabData } from "@/components/TabTile";
import { Button } from "@/components/ui/button";
import { Plus, Sun, Moon, ExternalLink, LayoutGrid, List } from "lucide-react";
import { useTheme } from "next-themes";
import { Link } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/use-toast";
import logo from "@/assets/logo.png";
import logoDark from "@/assets/logo-dark.png";
import PageFooter from "@/components/PageFooter";

// Mock data - will be replaced with backend data
const initialTabs: TabData[] = [
  {
    id: "1",
    url: "https://techcrunch.com/2025/11/06/tesla-shareholders-approve-elon-musks-1t-pay-package/",
    title: "Tesla shareholders approve Elon Musk's $1T pay package",
    description: "Breaking news from TechCrunch covering Tesla's historic shareholder vote on executive compensation.",
    thumbnail: "https://images.unsplash.com/photo-1560958089-b8a1929cea89?w=400&h=300&fit=crop",
  },
  {
    id: "2",
    url: "https://infobud.ai",
    title: "Infobud.ai - AI-Powered Information",
    description: "Discover AI-powered tools and insights for modern information management.",
    thumbnail: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=300&fit=crop",
  },
  {
    id: "3",
    url: "https://www.youtube.com/watch?v=WHoWGNQRXb0&list=PLW4bLg2h44Ah5OAG0jVE8XfuoDnaFpc6X",
    title: "YouTube Video - Playlist",
    description: "Watch this video from the curated playlist collection.",
    thumbnail: "https://img.youtube.com/vi/WHoWGNQRXb0/maxresdefault.jpg",
  },
  {
    id: "4",
    url: "https://www.youtube.com/watch?v=Rp3A5q9L_bg&list=PLW4bLg2h44Ah5OAG0jVE8XfuoDnaFpc6X&index=2",
    title: "YouTube Video - Part 2",
    description: "Continue watching the second video in this educational series.",
    thumbnail: "https://img.youtube.com/vi/Rp3A5q9L_bg/maxresdefault.jpg",
  },
  {
    id: "5",
    url: "https://www.youtube.com/watch?v=UHSkjro-VbE&list=PLW4bLg2h44Ah5OAG0jVE8XfuoDnaFpc6X&index=4",
    title: "YouTube Video - Part 4",
    description: "Advanced topics covered in the fourth installment of this video series.",
    thumbnail: "https://img.youtube.com/vi/UHSkjro-VbE/maxresdefault.jpg",
  },
  {
    id: "6",
    url: "https://www.youtube.com/watch?v=5GQldPDKh7o&list=PLWG9sUaFoiVp9I1thh3ZnbYXtIOvt3uXh",
    title: "YouTube Video - Tutorial Series",
    description: "Essential learning content from this comprehensive tutorial playlist.",
    thumbnail: "https://img.youtube.com/vi/5GQldPDKh7o/maxresdefault.jpg",
  },
];

const Index = () => {
  const [tabs, setTabs] = useState<TabData[]>(initialTabs);
  const [tabToRemove, setTabToRemove] = useState<string | null>(null);
  const [isMinimalistic, setIsMinimalistic] = useState(false);
  const { resolvedTheme, setTheme } = useTheme();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setTabs((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleRemove = (id: string) => {
    setTabToRemove(id);
  };

  const confirmRemove = () => {
    if (tabToRemove) {
      setTabs((items) => items.filter((item) => item.id !== tabToRemove));
      setTabToRemove(null);
      toast({
        title: "Tab removed",
        description: "The tab has been removed from your collection.",
      });
    }
  };

  const handleOpenAll = async () => {
    let successCount = 0;
    let failedCount = 0;

    // Open tabs with delays to preserve user activation and avoid popup blocking
    for (let i = 0; i < tabs.length; i++) {
      const tab = tabs[i];
      try {
        const opened = window.open(tab.url, '_blank', 'noopener,noreferrer');
        // window.open returns null if popup was blocked
        if (opened) {
          successCount++;
        } else {
          failedCount++;
        }
      } catch (error) {
        failedCount++;
      }

      // Add delay between opens (200ms) to help with popup blocking
      if (i < tabs.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }

    // Show results
    if (failedCount === 0) {
      toast({
        title: "Tabs opened",
        description: `Successfully opened ${successCount} tab${successCount === 1 ? '' : 's'}`,
      });
    } else if (successCount === 0) {
      toast({
        title: "Popup blocked",
        description: "Please allow popups for this site and try again by clicking 'Open All'.",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Partial success",
        description: `${successCount} tabs opened, ${failedCount} blocked. Allow popups and click 'Open All' again to open remaining tabs.`,
      });
    }
  };

  const handleAddTab = () => {
    toast({
      title: "Add tab",
      description: "Add tab functionality will be implemented by your backend.",
    });
  };

  return (
    <div className="min-h-screen bg-gradient-subtle flex flex-col">
      {/* Header */}
      <header className="glass-card border-b border-border/30 sticky top-0 z-10 shadow-elegant">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <img src={logoDark} alt="untabbed.to logo" className="h-8 w-8 dark:hidden" />
              <img src={logo} alt="untabbed.to logo" className="h-8 w-8 hidden dark:block" />
              <h1 className="text-3xl font-display font-bold text-black dark:text-white tracking-tight">
                untabbed.to
              </h1>
            </div>
            
            {/* Toolbar */}
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
              >
                {resolvedTheme === "dark" ? (
                  <Sun className="h-4 w-4 mr-2" />
                ) : (
                  <Moon className="h-4 w-4 mr-2" />
                )}
                {resolvedTheme === "dark" ? "Light" : "Dark"} Mode
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsMinimalistic(!isMinimalistic)}
              >
                {isMinimalistic ? (
                  <LayoutGrid className="h-4 w-4 mr-2" />
                ) : (
                  <List className="h-4 w-4 mr-2" />
                )}
                {isMinimalistic ? "Grid" : "Minimal"}
              </Button>
              <Button variant="outline" size="sm" onClick={handleOpenAll}>
                <ExternalLink className="h-4 w-4 mr-2" />
                Open All
              </Button>
              <Button size="sm" className="shadow-glow" onClick={handleAddTab}>
                <Plus className="h-4 w-4 mr-2" />
                Add Tab
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 flex-1">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={tabs} strategy={rectSortingStrategy}>
            <div className={isMinimalistic ? "space-y-2" : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"}>
              {tabs.map((tab) => (
                <TabTile key={tab.id} tab={tab} onRemove={handleRemove} minimalistic={isMinimalistic} />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        {tabs.length === 0 && (
          <div className="text-center py-20">
            <p className="text-muted-foreground text-lg">
              No tabs in this collection yet. Add some to get started!
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <PageFooter />

      {/* Confirmation Dialog */}
      <AlertDialog open={!!tabToRemove} onOpenChange={() => setTabToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this tab?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The tab will be permanently removed from your collection.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemove}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Index;
